// Saisie « Montant TVA » : déduit les bases HT par taux à partir de la TVA saisie
// et absorbe l'écart d'arrondi avec le « Montant de base HT » saisi.
//
// La TVA d'un devis est arrondie au centime ; la diviser par le taux amplifie
// l'erreur (×10 à 10 %, ×18 à 5,5 %, ×5 à 20 %). Toute base b telle que
// round(b × taux, 2) = TVA est aussi juste que la base déduite : on choisit,
// dans ces intervalles, les bases dont la somme vaut le HT saisi, en commençant
// par le taux le plus bas (intervalle le plus large).
//
// Calcul en centimes entiers, arrondi « half away from zero ». Miroir exact de
// MultiVat::TvaInputReconciler (app/services/multi_vat/tva_input_reconciler.rb),
// qui refait le calcul côté serveur : les deux doivent rester alignés.

const toCents = (euros) => Math.sign(euros) * Math.round(Math.abs(euros) * 100);

// round(n / d) half away from zero, d > 0.
const roundDiv = (n, d) => Math.sign(n) * Math.floor((2 * Math.abs(n) + d) / (2 * d));

/**
 * @param {Array<{rate: number, tva: number}>} lines  taux en %, TVA en euros (non nulle)
 * @param {number|null} targetHt  HT saisi en euros, ou null (HT = Σ bases déduites)
 * @returns {{reachable: boolean, bases: number[], ht: number, ttc: number, deducedHt: number, gap: number}}
 *   bases dans l'ordre de `lines`, montants en euros.
 */
export function reconcileVatBases(lines, targetHt) {
  const work = lines.map((line, index) => {
    const permille = Math.round(line.rate * 10); // 5,5 % → 55
    const tva = toCents(line.tva);
    const abs = Math.abs(tva);
    let min = Math.ceil((1000 * abs - 500) / permille);
    let max = Math.ceil((1000 * abs + 500) / permille) - 1;
    if (tva < 0) [min, max] = [-max, -min];
    return { index, rate: line.rate, tva, base: roundDiv(1000 * tva, permille), min, max };
  });

  const sum = (key) => work.reduce((acc, line) => acc + line[key], 0);
  const deduced = sum('base');
  const target = targetHt === null ? deduced : toCents(targetHt);
  const reachable = sum('min') <= target && target <= sum('max');

  if (reachable) {
    let delta = target - deduced;
    [...work]
      .sort((a, b) => a.rate - b.rate)
      .forEach((line) => {
        if (delta === 0) return;
        const room = delta > 0 ? line.max - line.base : line.min - line.base;
        const move = delta > 0 ? Math.min(delta, room) : Math.max(delta, room);
        line.base += move;
        delta -= move;
      });
  }

  const ht = reachable ? target : deduced;
  return {
    reachable,
    bases: work.map((line) => line.base / 100),
    ht: ht / 100,
    ttc: (ht + sum('tva')) / 100,
    deducedHt: deduced / 100,
    gap: (target - deduced) / 100,
  };
}
